import { useState, useEffect, useCallback, Dispatch, SetStateAction, useRef } from 'react';
// @mui
import Collapse from '@mui/material/Collapse';
// routes
import { useActiveLink } from 'src/routes/hooks/use-active-link';
//
import { Popover } from '@mui/material';
import { NavListProps, NavConfigProps } from '../types';
import NavItem from './nav-item';

// ----------------------------------------------------------------------

type NavListRootProps = {
  data: NavListProps;
  depth: number;
  hasChild: boolean;
  config: NavConfigProps;
};

type setOpenType = Dispatch<SetStateAction<boolean>>;
type toggleObserverType = {
  setOpen: setOpenType,
  depth: number,
}
let toToggle: toggleObserverType[] = [];
function registerOnToggle(setOpen: setOpenType, depth: number) {
  toToggle.push({ setOpen, depth });
}
function deregisterOnToggle(setOpen: setOpenType) {
  toToggle = toToggle.filter(a => a.setOpen !== setOpen);
}
function triggerOnToggle(setOpen: setOpenType, depth: number) {
  setOpen((prev) => !prev);
  toToggle.forEach(s => {
    if (s.setOpen !== setOpen && s.depth === depth) {
      s.setOpen(false)
    }
  })
}

export default function NavList({ data, depth, hasChild, config, collapsedDisplay = false, hideIcon = false }: NavListRootProps & {collapsedDisplay?: boolean, hideIcon?: boolean}) {
  const active = useActiveLink(data.path, hasChild);
  const externalLink = data.path?.includes('http');
  const [open, setOpen] = useState(active);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const itemRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!active) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    registerOnToggle(setOpen, depth);
    return () => deregisterOnToggle(setOpen);
  }, [depth]);

  const handleToggle = useCallback(() => {
    if (!collapsedDisplay) {
      triggerOnToggle(setOpen, depth);
    }
  }, [depth, collapsedDisplay]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleMouseEnter = useCallback(() => {

    if (collapsedDisplay) {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      setAnchorEl(itemRef.current);
    }
  }, [collapsedDisplay]);

  const handleMouseLeave = useCallback(() => {
    if (collapsedDisplay) {
      hoverTimeoutRef.current = setTimeout(() => {
        setAnchorEl(null);
      }, 100); // Small delay to allow moving to popover
    }
  }, [collapsedDisplay]);

  const handlePopoverMouseEnter = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  }, []);

  const handlePopoverMouseLeave = useCallback(() => {
    setAnchorEl(null);
  }, []);

  useEffect(() => () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  }, []);

  return (
    <>
      <div
        ref={itemRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <NavItem
          item={data}
          depth={depth}
          open={open}
          active={active}
          externalLink={externalLink}
          onClick={handleToggle}
          config={config}
          collapsedDisplay={collapsedDisplay}
          hideIcon={hideIcon}
        />
      </div>

      {hasChild && !collapsedDisplay && (
        <Collapse in={open} unmountOnExit>
          <NavSubList data={data.children} depth={depth} config={config} />
        </Collapse>
      )}
      {collapsedDisplay && anchorEl && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          disableRestoreFocus
          disableScrollLock
          sx={{
            pointerEvents: 'none',
          }}
          onMouseEnter={handlePopoverMouseEnter}
          onMouseLeave={handlePopoverMouseLeave}
          slotProps={{paper: {sx: {
            minWidth: 200,
            border: "1px solid #AAA",
            pointerEvents: 'auto',
            backgroundImage: "none"
          }}}}
        >
          {hasChild ? <NavSubList
            data={data.children}
            depth={depth}
            config={config}
            hideIcon
          /> : <NavSubList
            data={[{
              title: data.title,
              path: data.path,
            }]}
            hideIcon
            depth={depth}
            config={config}
          />}
        </Popover>
      )}
    </>
  );
}

// ----------------------------------------------------------------------

type NavListSubProps = {
  data: NavListProps[];
  depth: number;
  config: NavConfigProps;
  hideIcon?: boolean;
};

function NavSubList({ data, depth, config, hideIcon = false }: NavListSubProps) {
  return (
    <>
      {data.map((list) => (
        <NavList
          key={list.title + list.path}
          data={list}
          depth={depth + 1}
          hasChild={!!list.children}
          config={config}
          hideIcon={hideIcon}
        />
      ))}
    </>
  );
}
